import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, StateToken, Store } from '@ngxs/store';
import { switchMap, tap } from 'rxjs';
import { UserMedicineRoutes } from 'src/app/core/enums/routes.enum';
import { ResetCurrentFilter } from 'src/app/core/state/main-table-filter.actions';
import { MenuTreeStructureState } from 'src/app/core/state/menu-tree-structure.state';
import { AlertStatus } from 'src/app/risk-assessment-user/enums/alert-status.enum';
import { OnSuccess } from 'src/app/shared/decorators/on-success.decorator';
import { redirectAndBypassUnsavedDataCheck } from 'src/app/shared/functions/redirect-and-bypass-unsaved-data-check';
import { IdName } from 'src/app/shared/models/id-name';
import { MedicineStatus } from '../enums/medicine-status.enum';
import { getLinkToMedicineList } from '../functions/get-link-to-medicine-list';
import { IMedicineStock, IPagedMedicineStock } from '../models/medicine-stock.model';
import {
  IApiResponseGroupMedicines,
  IMedicineGroupData,
  IMedicineResponse,
} from '../models/medicine.model';
import { MedicineApiService } from '../services/medicine-api.service';
import {
  AddToStock,
  AdjustStock,
  ClearMedicinesByMenu,
  CreateMedicine,
  DeleteMedicine,
  LoadAllMedicines,
  LoadMedicineById,
  LoadMedicineStock,
  LoadPagedMedicines,
  PrintMedicineReport,
  UpdateMedicine,
  UseMedicine,
  ClearCurrentMedicine,
} from './medicine.actions';

export interface MedicineStateModel {
  medicines: IMedicineResponse[];
  medicinesGrouped: IMedicineGroupData[];
  allMedicines: IMedicineResponse[];
  allMedicinesPerDepartment: IdName[];
  medicinesTotalPages: number;
  currentMedicine: IMedicineResponse | undefined;
  medicineStock: IMedicineStock[];
  medicineStockTotalPages: number;
  medicineReport: Blob;
}

const MEDICINE_STATE_TOKEN = new StateToken<MedicineStateModel>('medicines');

@State<MedicineStateModel>({
  name: MEDICINE_STATE_TOKEN,
  defaults: {
    medicines: [],
    medicinesGrouped: [],
    allMedicines: [],
    allMedicinesPerDepartment: [],
    medicinesTotalPages: 0,
    currentMedicine: undefined,
    medicineStock: [],
    medicineStockTotalPages: 0,
    medicineReport: <Blob>{},
  },
})
@Injectable()
export class MedicineState {
  constructor(
    private service: MedicineApiService,
    private store: Store
  ) {}

  @Action(LoadPagedMedicines, { cancelUncompleted: true })
  loadPagedMedicines(
    { getState, setState }: StateContext<MedicineStateModel>,
    { filter, menuId }: LoadPagedMedicines
  ) {
    return this.service.getPagedMedicines(filter, menuId).pipe(
      tap((response: IApiResponseGroupMedicines) => {
        const state = getState();
        setState({
          ...state,
          medicines: response.data,
          medicinesGrouped: response.groupData,
          medicinesTotalPages: response.totalPages,
        });
      })
    );
  }

  @Action(LoadAllMedicines, { cancelUncompleted: true })
  loadAllMedicines({ getState, setState }: StateContext<MedicineStateModel>) {
    return this.service.getAllMedicines().pipe(
      tap((response: IMedicineResponse[]) => {
        const state = getState();
        setState({
          ...state,
          allMedicines: response,
        });
      })
    );
  }

  @Action(LoadMedicineById, { cancelUncompleted: true })
  loadMedicineById(
    { getState, setState }: StateContext<MedicineStateModel>,
    { id }: LoadMedicineById
  ) {
    return this.service.getMedicineById(id).pipe(
      tap((response: IMedicineResponse) => {
        const state = getState();
        setState({
          ...state,
          currentMedicine: response,
        });
      })
    );
  }

  @Action(CreateMedicine, { cancelUncompleted: true })
  @OnSuccess({ message: 'medicineCreatedToast' })
  createMedicine(ctx: StateContext<MedicineStateModel>, { medicine }: CreateMedicine) {
    return this.service.createMedicine(medicine).pipe(
      tap(() => {
        const menuId =
          this.store.selectSnapshot(MenuTreeStructureState.getSelectedMenuId) ?? medicine.menuId;
        redirectAndBypassUnsavedDataCheck(getLinkToMedicineList(menuId));
      })
    );
  }

  @Action(UpdateMedicine, { cancelUncompleted: true })
  @OnSuccess({ message: 'medicineUpdatedToast' })
  updateMedicine(ctx: StateContext<MedicineStateModel>, { medicine }: UpdateMedicine) {
    return this.service.updateMedicine(medicine).pipe(
      tap(() => {
        redirectAndBypassUnsavedDataCheck(`${UserMedicineRoutes.Overview}/${medicine.id}`);
      })
    );
  }

  @Action(DeleteMedicine, { cancelUncompleted: true })
  @OnSuccess({ message: 'medicineDeletedToast', dispatch: ResetCurrentFilter })
  deleteMedicine(ctx: StateContext<MedicineStateModel>, { medicineId }: DeleteMedicine) {
    const menuId = ctx.getState().currentMedicine?.menu?.id;
    return this.service.deleteMedicine(medicineId).pipe(
      tap(() => {
        redirectAndBypassUnsavedDataCheck(getLinkToMedicineList(menuId));
      })
    );
  }

  @Action(UseMedicine, { cancelUncompleted: true })
  @OnSuccess({ message: 'medicineUsed' })
  useMedicine(ctx: StateContext<MedicineStateModel>, { medicine }: UseMedicine) {
    return this.service.useMedicine(medicine).pipe(
      switchMap(() => this.service.getMedicineById(medicine.id)),
      tap((updatedMedicine) => {
        this.updateMedicineInState(ctx, updatedMedicine);
      })
    );
  }

  @Action(AddToStock, { cancelUncompleted: true })
  @OnSuccess({ message: 'medicineAddedToStock' })
  addToStock(ctx: StateContext<MedicineStateModel>, { medicine, redirect }: AddToStock) {
    return this.service.addtoStock(medicine).pipe(
      switchMap(() => this.service.getMedicineById(medicine.id)),
      tap((updatedMedicine) => {
        this.updateMedicineInState(ctx, updatedMedicine);
        if (redirect) {
          redirectAndBypassUnsavedDataCheck(`${UserMedicineRoutes.Overview}/${medicine.id}`);
        }
      })
    );
  }

  @Action(AdjustStock, { cancelUncompleted: true })
  @OnSuccess({ message: 'medicineStockAdjusted' })
  adjustStock(ctx: StateContext<MedicineStateModel>, { medicine, redirect }: AdjustStock) {
    return this.service.adjustStock(medicine).pipe(
      switchMap(() => this.service.getMedicineById(medicine.id)),
      tap((updatedMedicine) => {
        this.updateMedicineInState(ctx, updatedMedicine);
        if (redirect) {
          redirectAndBypassUnsavedDataCheck(`${UserMedicineRoutes.Overview}/${medicine.id}`);
        }
      })
    );
  }

  @Action(LoadMedicineStock, { cancelUncompleted: true })
  getMedicineStock(
    { getState, setState }: StateContext<MedicineStateModel>,
    { medicineId, filter, stockVersionId }: LoadMedicineStock
  ) {
    return this.service.getMedicineStock(medicineId, filter, stockVersionId).pipe(
      tap((response: IPagedMedicineStock) => {
        const state = getState();
        setState({
          ...state,
          medicineStock: response.data,
          medicineStockTotalPages: response.totalPages,
        });
      })
    );
  }

  @Action(PrintMedicineReport, { cancelUncompleted: true })
  printMedicineReport(
    { getState, setState }: StateContext<MedicineStateModel>,
    { filter, menuId }: PrintMedicineReport
  ) {
    return this.service.printReport(filter, menuId).pipe(
      tap((file: Blob) => {
        const state = getState();
        setState({
          ...state,
          medicineReport: file,
        });
      })
    );
  }

  @Action(ClearMedicinesByMenu, { cancelUncompleted: true })
  clearMedicinesByMenu(ctx: StateContext<MedicineStateModel>) {
    ctx.setState({
      ...ctx.getState(),
      medicines: [],
      medicinesGrouped: [],
      medicinesTotalPages: 0,
    });
  }

  @Action(ClearCurrentMedicine)
  clearCurrentMedicine(ctx: StateContext<MedicineStateModel>) {
    ctx.setState({
      ...ctx.getState(),
      currentMedicine: undefined,
    });
  }

  @Selector()
  static getMedicinesTotalPages(state: MedicineStateModel) {
    return state.medicinesTotalPages;
  }

  @Selector()
  static getMedicineById(state: MedicineStateModel): IMedicineResponse | undefined {
    return state.currentMedicine;
  }

  @Selector()
  static getMedicines(state: MedicineStateModel): IMedicineResponse[] {
    return state.medicines;
  }

  @Selector()
  static getMedicinesGrouped(state: MedicineStateModel): IMedicineGroupData[] {
    return state.medicinesGrouped;
  }

  @Selector()
  static getAllMedicines(state: MedicineStateModel): IMedicineResponse[] {
    return state.allMedicines;
  }

  @Selector()
  static getMedicineStock(state: MedicineStateModel): IMedicineStock[] {
    return state.medicineStock;
  }

  @Selector()
  static getMedicineStockTotalPages(state: MedicineStateModel): number {
    return state.medicineStockTotalPages;
  }

  @Selector()
  static getMedicineStockById(state: MedicineStateModel) {
    return (id: string) => {
      return state.medicineStock?.find((medicineStock: IMedicineStock) => medicineStock.id === id);
    };
  }

  @Selector()
  static getMedicineReport(state: MedicineStateModel): Blob {
    return state.medicineReport;
  }

  private updateMedicineInState(
    ctx: StateContext<MedicineStateModel>,
    updatedMedicine: IMedicineResponse
  ): void {
    const state = ctx.getState();
    const replaceMedicine = (medicine: IMedicineResponse) =>
      medicine.id === updatedMedicine.id
        ? this.mergeMedicineForList(updatedMedicine, medicine)
        : medicine;

    ctx.setState({
      ...state,
      currentMedicine: this.mergeMedicineForList(updatedMedicine, state.currentMedicine),
      medicines: state.medicines.map(replaceMedicine),
      allMedicines: state.allMedicines.map(replaceMedicine),
      medicinesGrouped: state.medicinesGrouped.map((group) => ({
        ...group,
        itemsOnNode: group.itemsOnNode.map(replaceMedicine),
      })),
    });
  }

  private mergeMedicineForList(
    updatedMedicine: IMedicineResponse,
    existing?: IMedicineResponse
  ): IMedicineResponse {
    return {
      ...existing,
      ...updatedMedicine,
      stockStatus: this.getStockStatus(updatedMedicine),
      expiryStatus: this.getExpiryStatus(updatedMedicine),
    };
  }

  private getStockStatus(medicine: IMedicineResponse): MedicineStatus {
    if (medicine.minimumQuantity > medicine.currentQuantity) {
      return MedicineStatus.Red;
    }

    return medicine.thresholdQuantity > medicine.currentQuantity
      ? MedicineStatus.Yellow
      : MedicineStatus.Green;
  }

  private getExpiryStatus(medicine: IMedicineResponse): AlertStatus {
    const batches = medicine.stockBatches ?? [];
    if (batches.some((batch) => batch.expiryStatus === AlertStatus.Expired)) {
      return AlertStatus.Expired;
    }
    if (batches.some((batch) => batch.expiryStatus === AlertStatus.Expiring)) {
      return AlertStatus.Expiring;
    }
    if (batches.length || !medicine.expires) {
      return AlertStatus.Ok;
    }

    return new Date(medicine.expires).getTime() <= Date.now()
      ? AlertStatus.Expired
      : AlertStatus.Ok;
  }
}
